import type {
  AgentConnectionStatus,
  AgentConnectionSummaryResponse,
} from '@contract-analyzer/api-client';

import { formatRelativeTime, formatShortDate } from '@/lib/format/date';

const INVITATION_LABEL = 'Connect your agent';
const CONNECTED_LABEL = 'Agent connected';

export function entryLabel(status: AgentConnectionStatus): string {
  return status === 'connected' ? CONNECTED_LABEL : INVITATION_LABEL;
}

const countFormatter = new Intl.NumberFormat('en-GB');

/** "2 computers · last used 3 days ago", or "No computers signed in" once every sign-in is revoked. */
export function describeSignIns(connection: AgentConnectionSummaryResponse, now: Date): string {
  if (connection.active_sign_ins === 0) {
    return 'No computers signed in';
  }
  const noun = connection.active_sign_ins === 1 ? 'computer' : 'computers';
  const count = `${countFormatter.format(connection.active_sign_ins)} ${noun}`;
  if (connection.last_used_at === null) {
    return count;
  }
  return `${count} · last used ${formatRelativeTime(new Date(connection.last_used_at), now)}`;
}

export function describeConnectedSince(
  connection: AgentConnectionSummaryResponse,
  now: Date,
): string | null {
  return connection.first_connected_at === null
    ? null
    : formatShortDate(new Date(connection.first_connected_at), now);
}

export interface CelebrationInput {
  /** Status the provider last saw, or `null` before the first response. */
  readonly previousStatus: AgentConnectionStatus | null;
  readonly connection: AgentConnectionSummaryResponse;
}

/**
 * The connected confirmation plays once per user: when this session watches the
 * status flip, or on the first load after connecting elsewhere that nobody has
 * acknowledged yet.
 */
export function shouldCelebrateConnection({ previousStatus, connection }: CelebrationInput): boolean {
  if (connection.status !== 'connected') {
    return false;
  }
  return previousStatus === 'not_connected' || connection.acknowledged_at === null;
}
